import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import SubPageLayout from "../../components/shared/SubPageLayout";

import logger from '../../utils/logger';

const PortfolioReports = () => {
  const [selectedElements, setSelectedElements] = useState([]);
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reportName, setReportName] = useState("");
  const [showPortfolioCosts, setShowPortfolioCosts] = useState(false);

  // Library of available report elements
  const elementLibrary = [
    {
      id: "snapshot",
      title: "Snapshot",
      description:
        "A one-page overview of the portfolio including asset classification, costs, and retirement map",
      required: true,
      icon: "📊",
    },
    {
      id: "performance-analysis",
      title: "Performance Analysis",
      description: "A modeled performance chart & aggregated statistics",
      icon: "📈",
    },
    {
      id: "ips-intro",
      title: "Investment Policy Statement Introduction",
      description:
        "An overview of the purpose of the Investment Policy Statement",
      icon: "📝",
    },
    {
      id: "risk-tolerance",
      title: "Risk Tolerance",
      description:
        "Overview of the client's Risk Number, risk tolerance and financial goals",
      icon: "⚠️",
    },
    {
      id: "asset-classification",
      title: "Asset Classification",
      description: "Stocks, Bonds, Cash, and Other investments breakdown",
      icon: "🎯",
    },
  ];

  // Handle element search
  const filteredElements = elementLibrary.filter((element) => {
    const matchesSearch = element.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    if (showUnusedOnly) {
      return (
        matchesSearch && !selectedElements.find((e) => e.id === element.id)
      );
    }
    return matchesSearch;
  });

  // Handle drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(selectedElements);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedElements(items);
  };

  // Add element to report
  const handleAddElement = (element) => {
    if (!selectedElements.find((e) => e.id === element.id)) {
      setSelectedElements([...selectedElements, element]);
    }
  };

  // Remove element from report
  const handleRemoveElement = (elementId) => {
    setSelectedElements(selectedElements.filter((e) => e.id !== elementId));
  };

  // Generate report
  const handleGenerateReport = () => {
    // Validate required elements
    const hasSnapshot = selectedElements.some((e) => e.id === "snapshot");
    if (!hasSnapshot) {
      alert("Snapshot is required in the report");
      return;
    }

    // TODO: Implement report generation logic
    logger.info("Generating report with elements:", selectedElements);
  };

  const breadcrumbItems = [
    { label: "Portfolio Management", path: "/portfolio-management" },
    {
      label: "Portfolio Reports",
      path: "/portfolio-management/portfolio-reports",
    },
  ];

  return (
    <SubPageLayout
      title="Portfolio Reports"
      subtitle="Create and customize portfolio reports"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="flex h-full">
        {/* Left Sidebar - Element Library */}
        <div className="w-80 bg-surface-default dark:bg-surface-default p-6 shadow-lg dark:shadow-none overflow-y-auto">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
            Element Library
          </h2>

          {/* Search and Filter */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search Elements by Name"
              className="w-full p-2 border rounded-lg mb-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showUnusedOnly}
                onChange={(e) => setShowUnusedOnly(e.target.checked)}
              />
              <span>Only Show Unused Elements</span>
            </label>
          </div>

          {/* Element List */}
          <div className="space-y-4">
            {filteredElements.map((element) => (
              <div
                key={element.id}
                className="bg-surface-default dark:bg-surface-default border rounded-lg p-4 hover:shadow-md dark:shadow-none transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="mr-2">{element.icon}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {element.title}
                    </span>
                  </div>
                  <button
                    onClick={() => handleAddElement(element)}
                    className="px-3 py-1 bg-[#029831] text-white rounded-lg hover:bg-[#018529] text-sm"
                  >
                    + ADD
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{element.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Center Panel - Report Preview */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
              Report Preview
            </h2>
            <div className="text-sm text-blue-600 bg-blue-50 p-4 rounded-lg mb-4">
              This preview uses example data and branding. Your data and
              branding will show on the generated report.
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="report-elements">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-4"
                  >
                    {selectedElements.map((element, index) => (
                      <Draggable
                        key={element.id}
                        draggableId={element.id}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="bg-gray-50 rounded-lg p-4 flex justify-between items-center"
                          >
                            <div className="flex items-center space-x-2">
                              <span>{element.icon}</span>
                              <span className="text-gray-900 dark:text-gray-100">
                                {element.title}
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveElement(element.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>

        {/* Right Sidebar - Settings */}
        <div className="w-80 bg-surface-default dark:bg-surface-default p-6 shadow-lg dark:shadow-none">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
            Report Settings
          </h2>

          {/* Client Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client
            </label>
            <button className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]">
              + ADD CLIENT
            </button>
          </div>

          {/* Portfolio Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Portfolio / Proposal
            </label>
            <button className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]">
              + ADD PORTFOLIO
            </button>
          </div>

          {/* Report Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Report Name
            </label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
              placeholder="Snapshot"
            />
          </div>

          {/* Comparison Toggle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Comparison
            </label>
            <button className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]">
              + ADD COMPARISON
            </button>
          </div>

          {/* Portfolio Costs Toggle */}
          <div className="mb-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showPortfolioCosts}
                onChange={(e) => setShowPortfolioCosts(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Show Tax Drag
              </span>
            </label>
          </div>

          {/* Generate Report Button */}
          <button
            onClick={handleGenerateReport}
            className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529] font-medium"
          >
            GENERATE REPORT
          </button>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default PortfolioReports;
